/**
 * Group Container Node Component
 * React Flow container for grouping nodes visually and logically
 */

import { memo, useState, useRef, useEffect } from 'react'
import { Handle, Position, NodeProps, NodeResizer, useReactFlow } from '@xyflow/react'
import { CONTAINER_LAYOUT, CONTAINER_OPACITY } from '../../config/containerStyles'
import type { ContainerType, CloudProvider } from '../../types/schema'

interface GroupContainerNodeData {
  label: string
  containerType: ContainerType
  provider?: CloudProvider
  icon?: string
  description?: string
  // Colors
  headerBgColor?: string  // Header background color (separate from body)
  headerTextColor?: string  // Header text color (separate from body text)
  bgColor: string  // Body background color
  textColor: string  // Body text color
  borderColor: string
  borderStyle?: 'solid' | 'dashed' | 'dotted'
  // Text overflow
  wordWrap?: boolean
  textOverflow?: string
  isDropTarget?: boolean  // Visual feedback when dragging over
  metadata?: Record<string, any>
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

function GroupContainerNode({ id, data, selected }: NodeProps) {
  const containerData = data as unknown as GroupContainerNodeData
  const { setNodes } = useReactFlow()

  // Inline editing state for label
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(containerData.label)
  const inputRef = useRef<HTMLInputElement>(null)

  // Inline editing state for description
  const [isEditingDesc, setIsEditingDesc] = useState(false)
  const [descValue, setDescValue] = useState(containerData.description || '')
  const descRef = useRef<HTMLTextAreaElement>(null)

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Focus description textarea when entering edit mode
  useEffect(() => {
    if (isEditingDesc && descRef.current) {
      descRef.current.focus()
      descRef.current.select()
    }
  }, [isEditingDesc])

  // Update local edit value when container label changes externally
  useEffect(() => {
    setEditValue(containerData.label)
  }, [containerData.label])

  // Update local description value when it changes externally
  useEffect(() => {
    setDescValue(containerData.description || '')
  }, [containerData.description])

  // Handle double-click to start editing
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  // Save changes
  const saveEdit = () => {
    if (editValue.trim() !== containerData.label) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, label: editValue.trim() } }
            : node
        )
      )
    }
    setIsEditing(false)
  }

  // Cancel editing
  const cancelEdit = () => {
    setEditValue(containerData.label)
    setIsEditing(false)
  }

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  // Handle click outside (blur)
  const handleBlur = () => {
    saveEdit()
  }

  // Description editing handlers
  const handleDescDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditingDesc(true)
  }

  const saveDescEdit = () => {
    const trimmed = descValue.trim()
    if (trimmed !== containerData.description) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, description: trimmed } }
            : node
        )
      )
    }
    setIsEditingDesc(false)
  }

  const cancelDescEdit = () => {
    setDescValue(containerData.description || '')
    setIsEditingDesc(false)
  }

  const handleDescKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      saveDescEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelDescEdit()
    }
  }

  const borderStyle = (containerData as any).borderStyle || containerData.borderStyle || 'solid'
  const isDropTarget = containerData.isDropTarget || false

  // Header colors (separate from body)
  const headerBgColor = (containerData as any).headerBgColor !== undefined ? (containerData as any).headerBgColor : containerData.bgColor
  const headerTextColor = (containerData as any).headerTextColor !== undefined ? (containerData as any).headerTextColor : containerData.textColor

  // Text styling
  const fontSize = (containerData as any).fontSize || CONTAINER_LAYOUT.FONT_SIZE
  const fontWeight = (containerData as any).fontWeight || CONTAINER_LAYOUT.FONT_WEIGHT
  const fontFamily = (containerData as any).fontFamily || 'Inter, sans-serif'
  const textAlign = (containerData as any).textAlign || 'left'
  const lineHeight = (containerData as any).lineHeight || 1.5
  const textDecoration = (containerData as any).textDecoration || 'none'
  const letterSpacing = (containerData as any).letterSpacing || 0
  const textOpacity = (containerData as any).textOpacity !== undefined ? (containerData as any).textOpacity : 1

  // Text overflow
  const wordWrap = (containerData as any).wordWrap !== undefined ? (containerData as any).wordWrap : false
  const textOverflow = (containerData as any).textOverflow || 'ellipsis'

  // Shadow settings
  const hasShadow = (containerData as any).shadow === true
  const shadowBlur = (containerData as any).shadowBlur || 4

  // Draw.io-style grouping metadata (tree-based hierarchy)
  const groupData = (containerData as any)?.group
  const groupDepth = groupData?.depth

  // Min/max resize limits
  const minWidth = containerData.minWidth || 100
  const minHeight = containerData.minHeight || 100
  const maxWidth = containerData.maxWidth || 2000
  const maxHeight = containerData.maxHeight || 2000

  // Calculate border color (always use base color, selection uses box-shadow)
  const getBorderColor = () => {
    if (isDropTarget) {
      return '#3B82F6' // Blue when drop target
    }
    return containerData.borderColor || 'transparent'
  }

  // Calculate border width based on state
  const getBorderWidth = () => {
    if (isDropTarget) return 3
    // Read from data first, fallback to constant
    const width = (containerData as any).borderWidth
    return width !== undefined ? width : CONTAINER_LAYOUT.BORDER_WIDTH
  }

  // Calculate box shadow for selection, drop target, and user shadow
  const getBoxShadow = () => {
    const shadows = []

    if (isDropTarget) {
      shadows.push('0 0 0 3px rgba(59, 130, 246, 0.2)')
    } else if (selected) {
      shadows.push('0 0 0 3px #f59e0b')
    }

    if (hasShadow) {
      shadows.push(`0 ${shadowBlur}px ${shadowBlur * 2}px rgba(0, 0, 0, 0.2)`)
    }

    return shadows.length > 0 ? shadows.join(', ') : 'none'
  }

  return (
    <div
      className="group-container-node"
      data-group-depth={groupDepth}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '100px', // Allow shrinking
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${CONTAINER_LAYOUT.BORDER_RADIUS}px`,
        overflow: 'visible', // Changed from 'hidden' to allow resize
        transition: 'all 0.2s ease',
        boxShadow: getBoxShadow()
      }}
    >
      {/* Resize handles */}
      <NodeResizer
        isVisible={selected}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: 0,
          backgroundColor: '#3b82f6',
          border: '2px solid white',
        }}
        lineStyle={{
          borderWidth: 1,
          borderColor: '#3b82f6',
        }}
      />

      {/* Header - Solid background for readability */}
      <div
        className="container-header"
        style={{
          display: 'flex',
          height: `${CONTAINER_LAYOUT.HEADER_HEIGHT}px`,
          padding: CONTAINER_LAYOUT.HEADER_PADDING,
          backgroundColor: headerBgColor,
          color: headerTextColor,
          borderBottom: getBorderWidth() === 0 ? 'none' : `1px solid ${darkenColor(containerData.borderColor, 10)}`,
          alignItems: 'center',
          gap: '8px',
          fontSize: `${fontSize}px`,
          fontWeight: fontWeight,
          fontFamily: fontFamily,
          lineHeight: lineHeight,
          textDecoration: textDecoration,
          letterSpacing: `${letterSpacing}px`,
          opacity: textOpacity,
          cursor: 'move',
          userSelect: isEditing ? 'auto' : 'none'
        }}
      >
        {/* Icon */}
        {containerData.icon && (
          containerData.icon.startsWith('/icons/') ? (
            <img
              src={containerData.icon}
              alt={containerData.label}
              style={{
                width: `${CONTAINER_LAYOUT.ICON_SIZE}px`,
                height: `${CONTAINER_LAYOUT.ICON_SIZE}px`,
                objectFit: 'contain',
                flexShrink: 0
              }}
            />
          ) : (
            <span style={{ fontSize: `${CONTAINER_LAYOUT.ICON_SIZE}px`, flexShrink: 0 }}>
              {containerData.icon}
            </span>
          )
        )}

        {/* Label - Editable on double-click */}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              fontSize: `${fontSize}px`,
              fontWeight: fontWeight,
              fontFamily: fontFamily,
              textAlign: textAlign as any,
              border: '2px solid #3b82f6',
              borderRadius: '4px',
              padding: '2px 6px',
              background: 'white',
              color: containerData.textColor,
              outline: 'none',
            }}
          />
        ) : (
          <span
            onDoubleClick={handleDoubleClick}
            style={{
              flex: 1,
              whiteSpace: textOverflow === 'ellipsis' ? 'nowrap' : (wordWrap ? 'normal' : 'nowrap'),
              overflow: 'hidden',
              textOverflow: textOverflow as any,
              textAlign: textAlign as any,
              cursor: 'text'
            }}
          >
            {containerData.label}
          </span>
        )}

        {/* Provider badge (for cloud providers) */}
        {containerData.containerType === 'cloud-provider' && containerData.provider && (
          <span
            style={{
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              flexShrink: 0
            }}
          >
            {containerData.provider}
          </span>
        )}
      </div>

      {/* Body - Semi-transparent to see nodes through */}
      <div
        className="container-body"
        style={{
          flex: 1,
          position: 'relative',
          backgroundColor: containerData.bgColor === 'transparent'
            ? 'transparent'
            : (containerData as any).fillOpacity !== undefined
              ? `${containerData.bgColor}${opacityToHex((containerData as any).fillOpacity)}`
              : `${containerData.bgColor}${opacityToHex(CONTAINER_OPACITY.BODY)}`,
          border: `${getBorderWidth()}px ${borderStyle} ${getBorderColor()}`,
          borderTop: 'none',
          borderBottomLeftRadius: `${CONTAINER_LAYOUT.BORDER_RADIUS}px`,
          borderBottomRightRadius: `${CONTAINER_LAYOUT.BORDER_RADIUS}px`,
          padding: `${CONTAINER_LAYOUT.BODY_PADDING}px`,
          minHeight: 0  // Important for flex layout
        }}
      >
        {/* Description - Editable on double-click */}
        {isEditingDesc ? (
          <textarea
            ref={descRef}
            value={descValue}
            onChange={(e) => setDescValue(e.target.value)}
            onBlur={saveDescEdit}
            onKeyDown={handleDescKeyDown}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '8px',
              left: '12px',
              width: 'calc(100% - 24px)',
              minHeight: '60px',
              fontSize: '11px',
              color: containerData.textColor,
              border: '2px solid #3b82f6',
              borderRadius: '4px',
              padding: '6px',
              background: 'rgba(255, 255, 255, 0.95)',
              outline: 'none',
              resize: 'vertical',
              zIndex: 100,
            }}
            placeholder="Add description (double-click to edit)..."
          />
        ) : (
          <div
            onDoubleClick={handleDescDoubleClick}
            className={`gv2-container-desc${containerData.description ? '' : ' gv2-container-desc-empty'}`}
            style={{
              position: 'absolute',
              top: '8px',
              left: '12px',
              fontSize: '11px',
              color: darkenColor(containerData.textColor, 30),
              maxWidth: '60%',
              opacity: containerData.description ? 0.7 : undefined,
              pointerEvents: 'auto',
              cursor: 'text',
              whiteSpace: 'pre-wrap',
            }}
          >
            {containerData.description || 'Add description'}
          </div>
        )}
      </div>

      {/* Connection Handles - 12 points (draw.io style) */}
      {/* Top handles */}
      <Handle
        type="source"
        position={Position.Top}
        id="top-left-source"
        style={{ ...handleStyle, left: '25%', top: 0 }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-left-target"
        style={{ ...handleStyle, left: '25%', top: 0 }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-center-source"
        style={{ ...handleStyle, left: '50%', top: 0 }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-center-target"
        style={{ ...handleStyle, left: '50%', top: 0 }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-right-source"
        style={{ ...handleStyle, left: '75%', top: 0 }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-right-target"
        style={{ ...handleStyle, left: '75%', top: 0 }}
      />

      {/* Right handles */}
      <Handle
        type="source"
        position={Position.Right}
        id="right-top-source"
        style={{ ...handleStyle, top: '25%' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-top-target"
        style={{ ...handleStyle, top: '25%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-center-source"
        style={{ ...handleStyle, top: '50%' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-center-target"
        style={{ ...handleStyle, top: '50%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-bottom-source"
        style={{ ...handleStyle, top: '75%' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-bottom-target"
        style={{ ...handleStyle, top: '75%' }}
      />

      {/* Bottom handles */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-left-source"
        style={{ ...handleStyle, left: '25%' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-left-target"
        style={{ ...handleStyle, left: '25%' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-center-source"
        style={{ ...handleStyle, left: '50%' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-center-target"
        style={{ ...handleStyle, left: '50%' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-right-source"
        style={{ ...handleStyle, left: '75%' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-right-target"
        style={{ ...handleStyle, left: '75%' }}
      />

      {/* Left handles */}
      <Handle
        type="source"
        position={Position.Left}
        id="left-top-source"
        style={{ ...handleStyle, top: '25%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-top-target"
        style={{ ...handleStyle, top: '25%' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-center-source"
        style={{ ...handleStyle, top: '50%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-center-target"
        style={{ ...handleStyle, top: '50%' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-bottom-source"
        style={{ ...handleStyle, top: '75%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-bottom-target"
        style={{ ...handleStyle, top: '75%' }}
      />
    </div>
  )
}

// ============================================================================
// HELPER STYLES & FUNCTIONS
// ============================================================================

const handleStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#2563eb',
  border: '2px solid white',
  opacity: 0  // Hidden by default, shown on hover (via CSS)
}

/**
 * Convert opacity (0-1) to hex alpha channel (00-FF)
 */
function opacityToHex(opacity: number): string {
  const alpha = Math.round(opacity * 255)
  const hex = alpha.toString(16).padStart(2, '0')
  return hex
}

/**
 * Darken a hex color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  // Remove # if present
  hex = hex.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Darken
  const factor = 1 - percent / 100
  const newR = Math.round(r * factor)
  const newG = Math.round(g * factor)
  const newB = Math.round(b * factor)

  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`
}

export default memo(GroupContainerNode)
